import { defineMessages, IntlShape } from "react-intl";

import userDisplayName from "@mapmycustomers/shared/util/formatters/userDisplayName";

import PermissionsReportRow from "../types/PermissionsReportRow";

import columnMessages from "./columnMessages";
import getLocationStatusDisplayName from "./getLocationStatusDisplayName";
import getPermissionStatusDisplayName from "./getPermissionStatusDisplayName";
import getTeamNames from "./getTeamNames";

const messages = defineMessages({
  email: {
    id: "reports.permissionsReport.csv.column.email",
    defaultMessage: "Email",
    description: "Permissions Report export column - the rep's email address",
  },
  unknownDevice: {
    id: "reports.permissionsReport.csv.unknownDevice",
    defaultMessage: "Unknown device",
    description: "Export value when the rep's app build does not report which device it is",
  },
});

// A CSV has no expand affordance, so a multi-device rep contributes one row per device rather than
// a rolled-up row whose cells would read "Mixed".
const flatten = (rows: PermissionsReportRow[]): PermissionsReportRow[] =>
  rows.flatMap((row) => row.children ?? [row]);

const toCsvRow = (
  intl: IntlShape,
  row: PermissionsReportRow,
  supportsMileageTracking: boolean
): string[] => [
  userDisplayName(row.user),
  row.user.username,
  getTeamNames(row.user),
  row.deviceCount ? row.deviceModel || intl.formatMessage(messages.unknownDevice) : "",
  row.appVersion ?? "",
  getLocationStatusDisplayName(intl, row.location),
  getPermissionStatusDisplayName(intl, row.notifications),
  getPermissionStatusDisplayName(intl, row.preciseLocation),
  getPermissionStatusDisplayName(intl, row.backgroundRefresh),
  ...(supportsMileageTracking ? [getPermissionStatusDisplayName(intl, row.motion)] : []),
];

// Motion is left out for an org without Mileage Tracker, matching the table.
const buildPermissionsReportCsv = (
  intl: IntlShape,
  rows: PermissionsReportRow[],
  supportsMileageTracking: boolean
): string[][] => [
  [
    intl.formatMessage(columnMessages.name),
    intl.formatMessage(messages.email),
    intl.formatMessage(columnMessages.team),
    intl.formatMessage(columnMessages.device),
    intl.formatMessage(columnMessages.version),
    intl.formatMessage(columnMessages.location),
    intl.formatMessage(columnMessages.notifications),
    intl.formatMessage(columnMessages.preciseLocation),
    intl.formatMessage(columnMessages.backgroundRefresh),
    ...(supportsMileageTracking ? [intl.formatMessage(columnMessages.motion)] : []),
  ],
  ...flatten(rows).map((row) => toCsvRow(intl, row, supportsMileageTracking)),
];

export default buildPermissionsReportCsv;
